// Phase 6/10 — Prep mode. README §6.7.1, DECISIONS §E.3, §F.3.
//
// When the recipe declares BOTH preCook and prepCook phases the prep
// stage splits the landscape screen vertically: PreCook left, PrepCook
// right, each a full `PhasePane` with its own outstanding tabs,
// timeline, active dials, look-back slider and phase timer. When only
// one prep phase is declared, that phase takes the full width.
//
// End of phase: when a phase's timer expires its task area is replaced by
// a `PhaseEndPanel` — "<Phase> Time's Up!" + a "<Phase> Complete" checkbox
// that only enables once every task in the phase is checked. Prep → LiveCook
// fires once every present phase's Complete is ticked. (Split prep stacks
// Time's Up over Complete in each half; a single prep phase uses the
// full-width 1/3 ∶ 2/3 layout.)

import { useEffect, useMemo, useState } from 'react';
import type { ClassifiedPlan, ScheduleEntry } from '@cookchow/shared';
import { HandednessToggle, SpeakerToggle, TimerDisplay } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { useElapsedSeconds } from './useElapsedSeconds';
import { PhasePane } from './PhasePane';
import { PhaseEndPanel } from './PhaseEndPanel';
import { CookSettingsDrawer } from './CookSettingsDrawer';
import { buildNoteLookup } from './notes';
import { useAssist } from './useAssist';
import { completeTask as completeTaskApi } from '../api/client';
import { correctedNow, formatHHMMSS } from './helpers';
import { phaseBounds } from './phaseLayout';

interface PrepPaneProps {
  schedule: readonly ScheduleEntry[];
  classified: ClassifiedPlan | null;
  onTransition: (prepElapsedSeconds: number) => void;
}

function nominalSeconds(tasks: readonly ScheduleEntry[]): number {
  if (tasks.length === 0) return 0;
  const b = phaseBounds(tasks);
  return Math.max(0, Math.round((b.finishMs - b.startMs) / 1000));
}

export function PrepPane({ schedule, classified, onTransition }: PrepPaneProps) {
  const prepStartedAt = useAppStore((s) => s.prepStartedAt);
  const recipe = useAppStore((s) => s.recipe);
  const completedTaskIds = useAppStore((s) => s.completedTaskIds);
  const recipeFile = useAppStore((s) => s.recipeFile);
  const planToken = useAppStore((s) => s.planToken);
  const markTaskCompleted = useAppStore((s) => s.markTaskCompleted);
  const setClassified = useAppStore((s) => s.setClassified);
  const clockOffset = useAppStore((s) => s.clockOffset);
  const handedness = useAppStore((s) => s.handedness);
  const setHandedness = useAppStore((s) => s.setHandedness);
  const soundEnabled = useAppStore((s) => s.soundEnabled);
  const setSoundEnabled = useAppStore((s) => s.setSoundEnabled);

  const elapsed = useElapsedSeconds(prepStartedAt);

  const preCookTasks = useMemo(() => schedule.filter((e) => e.phase === 'preCook'), [schedule]);
  const prepCookTasks = useMemo(() => schedule.filter((e) => e.phase === 'prepCook'), [schedule]);
  const allPrepTasks = useMemo(
    () => schedule.filter((e) => e.phase === 'preCook' || e.phase === 'prepCook'),
    [schedule],
  );
  const bothPhases = preCookTasks.length > 0 && prepCookTasks.length > 0;
  const noteLookup = useMemo(() => buildNoteLookup(recipe), [recipe]);
  const { matcher: assistMatcher, resolveEntry: resolveAssistEntry } = useAssist();

  // The "<Phase> Complete" confirmations. Session-local — the pane is
  // mounted for the whole prep stage and unmounts at the transition.
  const [completed, setCompleted] = useState<{ preCook: boolean; prepCook: boolean }>({
    preCook: false,
    prepCook: false,
  });
  const completedSet = useMemo(() => new Set(completedTaskIds), [completedTaskIds]);

  // Prep → LiveCook the moment every PRESENT phase's Complete is ticked.
  useEffect(() => {
    const needed: boolean[] = [];
    if (preCookTasks.length > 0) needed.push(completed.preCook);
    if (prepCookTasks.length > 0) needed.push(completed.prepCook);
    if (needed.length > 0 && needed.every(Boolean)) onTransition(elapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed, preCookTasks.length, prepCookTasks.length]);

  const handleCheck = async (taskId: string): Promise<void> => {
    markTaskCompleted(taskId);
    if (!planToken) return;
    try {
      const res = await completeTaskApi({
        planToken,
        taskId,
        completedAt: new Date(correctedNow(clockOffset)).toISOString(),
      });
      setClassified(res.classified, res.nudge, res.serverNow);
    } catch {
      // Calm: offline chip handles the rest.
    }
  };

  const commonPaneProps = {
    classified,
    clockOffset,
    handedness,
    timelineMode: 'prep' as const,
    noteLookup,
    onCheck: handleCheck,
    assistMatcher: assistMatcher ?? undefined,
    resolveAssistEntry,
  };

  // The end-of-phase panel for a phase. Shown once the phase timer expires
  // OR every task is checked (an early finish). On an early finish only the
  // "<Phase> Complete" block shows; once the timer is up it gains the
  // "<Phase> Time's Up!" heading. `undefined` while neither holds.
  const endPanelFor = (
    label: string,
    tasks: readonly ScheduleEntry[],
    key: 'preCook' | 'prepCook',
    layout: 'horizontal' | 'stacked',
  ) => {
    if (tasks.length === 0) return undefined;
    const timesUp = elapsed >= nominalSeconds(tasks);
    const canComplete = tasks.every((t) => completedSet.has(t.id));
    if (!timesUp && !canComplete) return undefined;
    return (
      <PhaseEndPanel
        phaseLabel={label}
        layout={layout}
        timesUp={timesUp}
        checked={completed[key]}
        canComplete={canComplete}
        checkboxSide={handedness}
        onToggle={() => setCompleted((c) => ({ ...c, [key]: !c[key] }))}
      />
    );
  };

  // Top-bar timer total = the whole prep span (earliest prep start → latest
  // prep deadline). Elapsed counts up from when prep began.
  const prepTotalSeconds = useMemo(() => nominalSeconds(allPrepTasks), [allPrepTasks]);

  return (
    <main
      aria-label="Prep mode cooking screen"
      data-handedness={handedness}
      data-prep-split={bothPhases ? '' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        minHeight: '100vh',
      }}
    >
      <CookSettingsDrawer />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <span style={{ color: theme.text.primary, fontSize: '1rem', fontWeight: 600 }}>
          {recipeFile?.name ?? 'Prep in progress.'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <SpeakerToggle enabled={soundEnabled} onChange={setSoundEnabled} />
          <HandednessToggle value={handedness} onChange={setHandedness} />
          <TimerDisplay
            seconds={elapsed}
            totalSeconds={prepTotalSeconds || undefined}
            label="Prep"
          />
        </div>
      </div>

      {bothPhases ? (
        <div
          style={{
            display: 'flex',
            // Fixed column order: PreCook left, PrepCook right. Handedness
            // only flips the rail/checkbox side *inside* each column.
            flexDirection: 'row',
            gap: '1rem',
            alignItems: 'stretch',
          }}
        >
          <PhasePane
            {...commonPaneProps}
            tasks={preCookTasks}
            timelinePhaseChip="PreCook"
            ariaLabel="PreCook tasks"
            taskAreaOverride={endPanelFor('PreCook', preCookTasks, 'preCook', 'stacked')}
          />
          <div
            aria-hidden="true"
            style={{ width: '1px', background: theme.bg.muted, alignSelf: 'stretch' }}
          />
          <PhasePane
            {...commonPaneProps}
            tasks={prepCookTasks}
            timelinePhaseChip="PrepCook"
            ariaLabel="PrepCook tasks"
            taskAreaOverride={endPanelFor('PrepCook', prepCookTasks, 'prepCook', 'stacked')}
          />
        </div>
      ) : (
        <PhasePane
          {...commonPaneProps}
          tasks={allPrepTasks}
          timelinePhaseChip={preCookTasks.length > 0 ? 'PreCook' : 'PrepCook'}
          ariaLabel="Prep tasks"
          taskAreaOverride={endPanelFor(
            preCookTasks.length > 0 ? 'PreCook' : 'PrepCook',
            allPrepTasks,
            preCookTasks.length > 0 ? 'preCook' : 'prepCook',
            'horizontal',
          )}
        />
      )}

      <span
        aria-hidden="true"
        style={{ display: 'none' }}
        data-prep-elapsed={formatHHMMSS(elapsed)}
      />
    </main>
  );
}

export default PrepPane;
